// @flow
import type {Node} from 'react'
import React, {useState, useEffect} from 'react'
import ReactDatePicker, {registerLocale, setDefaultLocale} from 'react-datepicker'
import {enUS, arSA, fr, es, da, de, hi, ja, it, nl, pl, pt, ru, zhCN, enGB} from 'date-fns/locale'
import classNames from 'classnames'
import convertPosixToLdml from 'utils/MiscUtils/convertPosixToLdml'
import moment from 'moment-timezone'

// Global provided by legacy app bootstrap. Declare for Flow
declare var gf: any

const locales = {
  en: enUS,
  'en-GB': enGB,
  ar: arSA,
  fr,
  es,
  da,
  de,
  hi,
  ja,
  it,
  nl,
  pl,
  pt,
  ru,
  'zh-CN': zhCN,
}

function parseDateString(dateString) {
  if (!dateString)
    return null

  const date = moment.parseZone(dateString)
  const year = date.year()
  const month = date.month()
  const day = date.date()

  return new Date(year, month, day)
}

function formatSelectedDate(selectedDate, timeZone) {
  return moment.tz(
    [selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()],
    timeZone,
  ).format('MMMM D HH:mm:ss YYYY z')
}

type Props = $ReadOnly<{|
  name: string,
  dateString?: ?string,
  className?: ?string,
  placeholderText?: ?string,
  disabled?: boolean | string,
|}>

function RailsDatePicker({name, dateString, className, placeholderText, disabled}: Props): Node {
  const timeZone = gf.app.currentPersonTimeZone() || 'America/New_York'
  const dateFormat = convertPosixToLdml(I18n.t('date.formats.default_dashed'))
  const dateValue = parseDateString(dateString)
  const [date, setDate] = useState(dateValue)
  const [formattedDate, setFormattedDate] = useState(dateString)
  const locale = I18n.locale || 'en'

  useEffect(() => {
    if (locales[locale]) {
      registerLocale(locale, locales[locale])
      setDefaultLocale(locale)
    }
  }, [locale])

  const handleDateChange = (selectedDate) => {
    if (selectedDate) {
      const formatted = formatSelectedDate(selectedDate, timeZone)
      setDate(selectedDate)
      setFormattedDate(formatted)
    } else {
      setDate(null)
      setFormattedDate('')
    }
  }

  const disabledInput = () => {
    const inputClassName = classNames(className, 'disabled w-100')
    return (
      <input
        className={inputClassName}
        placeholder={placeholderText}
        disabled
      />
    )
  }

  if (disabled?.toString() === 'true')
    return disabledInput()

  return (
    <div className="rails-date-picker">
      <ReactDatePicker
        className={className}
        wrapperClassName="w-100"
        dateFormat={dateFormat}
        name={name}
        placeholderText={placeholderText}
        onChange={handleDateChange}
        selected={date}
        locale={locale}
      />
      <input type="hidden" name={name} value={formattedDate || ''} />
      <i className="fa-regular fa-calendar-days calendar-icon" />
    </div>
  )
}

export default RailsDatePicker
